import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import PmHeader from "./Partials/PmHeader/Header";
import SideNav from "./SideBar/SideNav";
import Dashboard from "./Pages/Dashboard/Dashboard";
import Clients from "./Pages/Clients/Clients";
import Projects from "./Pages/Projects/Projects";
import ProjectsRoot from "./Pages/Projects/ProjectsRoot";
import Teams from "./Pages/Teams/Teams";
import TimeEntry from "./Pages/TimeEntry/TimeEntry";
import Reports from "./Pages/Reports/Reports";
import Users from "./Pages/Users/Users";
import Settings from "./Pages/Settings/Settings";
import EditProfile from "./Pages/Profile/EditProfile";
import TeamProjects from "./Pages/Profile/EditProfile/TeamProjects";
import ChangePassword from "./Pages/Profile/ChangePassword";
import Login from "./Pages/Auth/Login";
import ForgotPassword from "./Pages/Auth/ForgotPassword";
import ResetPassword from "./Pages/Auth/ResetPassword";
import VerifyEmail from "./Pages/Auth/VerifyEmail";
import VerifyEmailSuccess from "./Pages/Auth/VerifyEmailSuccess";
import ForcePassword from "./Pages/Auth/ForcePassword";
import MfaLogin from "./Pages/Auth/MfaLogin";
import MfaSetup from "./Pages/Auth/MfaSetup";
import PageNotFound from "./Utility/PageNotFound/PageNotFound";
import ErrorBoundary from "./Utility/ErrorBoundary/ErrorBoundary";
import ProtectedRoute from "./Utility/ProtectedRoute/ProtectedRoute";
import { Toaster, ToastBar, toast as hotToast } from "react-hot-toast";
import { ROUTES, APP_DOCUMENT_TITLE } from "./constants";
import { getPostLoginRedirectPath } from "./permissions/postLoginRedirect";
import UseTitle from "./Utility/UseTitle/UseTitle";
import { useDispatch, useSelector } from "react-redux";
import { selectSidebarState, setSidebarState } from "./Redux/Slices/sidebarSlice";
import useMediaQuery from "./Utility/MediaQuery/useMediaQuery";
import Issues from "./Pages/Issues/Issues";
import WorkItemDetailPage from "./Pages/Issues/WorkItemDetailPage";
import WorkItemSharedDetailPage from "./Pages/Issues/WorkItemSharedDetailPage";
import BillingConfiguration from "./Pages/Billing/BillingConfiguration";
import Exports from "./Pages/Billing/Exports/Exports";
import SaasBilling from "./Pages/Billing/SaasBilling/SaasBilling";
import TabMapping from "./Pages/Billing/TabMapping";
import RolePermission from "./Pages/Settings/RolePermission";
import VendorTagMapping from "./Pages/Settings/vendor-tag-mapping/VendorTagMapping";
import SystemListsSettings from "./Pages/Settings/SystemLists/SystemListsSettings";
import RequirePermission from "./Utility/RequirePermission/RequirePermission";
import {
  PERMISSION_KEYS,
  TIME_ENTRY_TAB_VIEW_KEYS,
  SETTINGS_SYSTEM_LISTS_ANY_KEYS,
  CLIENTS_ROUTE_INDEX_ANY_KEYS,
  PROJECTS_ROUTE_WORKSPACE_ANY_KEYS,
  WORK_ITEMS_SIDEBAR_ANY_KEYS,
} from "./permissions/permissionKeys";
import GlobalApiErrorModal from "./Utility/GlobalApiErrorModal";

/** Old bookmarks: /settings/projects-management/edit/:id → /projects/edit/:id */
function LegacySettingsProjectEditRedirect() {
  const { id } = useParams();
  return <Navigate to={`${ROUTES.PROJECTS_EDIT}/${id}`} replace />;
}

/**
 * Legacy or mistaken URLs like `/users/0` or `/users/123` (not under `/users/edit/`).
 * Invalid integer ids (0, negative) → list; otherwise → canonical edit URL.
 */
function UsersBareIdRedirect() {
  const { id } = useParams();
  const s = id != null ? String(decodeURIComponent(id)) : "";
  const n = Number(s);
  const isIntegerString = /^-?\d+$/.test(s);
  if (isIntegerString && (!Number.isFinite(n) || n < 1)) {
    return <Navigate to={ROUTES.USERS} replace />;
  }
  return <Navigate to={`${ROUTES.USERS_EDIT}/${encodeURIComponent(s)}`} replace />;
}

/** `/` uses the same role-based landing as post-login (e.g. leads → projects list). */
function AuthenticatedHomeRedirect() {
  const me = useSelector((s) => s.auth?.me);
  const profileResolved = useSelector((s) => s.auth?.profileResolved);
  const authToken = typeof window !== "undefined" ? localStorage.getItem("authToken") : null;

  if (authToken && !profileResolved) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div
          className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-theme-primary border-t-transparent"
          aria-hidden
        />
      </div>
    );
  }

  return <Navigate to={getPostLoginRedirectPath(me)} replace />;
}

/** Sticky PmHeader is `h-16` — offset toasts so icon + text are not clipped behind the header. */
const TOAST_BELOW_HEADER_TOP = "4.25rem";

const TOAST_SURFACE_STYLE = {
  zIndex: 10050,
  pointerEvents: "auto",
  background: "#fff",
  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.12)",
  minWidth: "20rem",
  padding: "12px 14px",
};

function App() {
  const dispatch = useDispatch();
  const toggleNav = useSelector(selectSidebarState);
  const isSmTab = useMediaQuery("(max-width: 768px)");

  const closeSidebar = () => {
    if (isSmTab) {
      dispatch(setSidebarState(true));
    }
  };

  return (
    <ErrorBoundary>
      <Toaster
        position="top-right"
        containerStyle={{ zIndex: 10050, top: TOAST_BELOW_HEADER_TOP }}
        toastOptions={{
          duration: 10_000,
          success: { duration: 10_000 },
          error: { duration: 10_000 },
          loading: { duration: Infinity },
          style: TOAST_SURFACE_STYLE,
        }}
      >
        {(t) => (
          <ToastBar toast={t} position={t.position || "top-right"} style={TOAST_SURFACE_STYLE}>
            {({ icon, message }) => (
              <div className="flex w-full min-w-0 items-start gap-1">
                <span className="mt-0.5 shrink-0">{icon}</span>
                <span className="min-w-0 break-words text-sm leading-snug">{message}</span>
                <button
                  type="button"
                  className="-m-0.5 shrink-0 rounded p-1 text-gray-600 hover:bg-black/5 hover:text-gray-900"
                  aria-label="Dismiss notification"
                  onClick={() => hotToast.dismiss(t.id)}
                >
                  <span aria-hidden="true" className="block text-lg leading-none">
                    ×
                  </span>
                </button>
              </div>
            )}
          </ToastBar>
        )}
      </Toaster>
      <GlobalApiErrorModal />
      <Router>
        <Routes>
          <Route path={ROUTES.LOGIN} element={<Login />} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
          <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
          <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />
          <Route path={ROUTES.VERIFY_EMAIL_SUCCESS} element={<VerifyEmailSuccess />} />
          <Route path={ROUTES.FORCE_PASSWORD} element={<ForcePassword />} />
          <Route path={ROUTES.MFA_LOGIN} element={<MfaLogin />} />
          <Route path={ROUTES.MFA_SETUP} element={<MfaSetup />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <>
                  <UseTitle formattedTitle={APP_DOCUMENT_TITLE} />
                  {isSmTab && !toggleNav && (
                    <div
                      id="closeSidebar"
                      onClick={closeSidebar}
                      className="fixed inset-0 bg-black bg-opacity-50 z-[45]"
                    ></div>
                  )}
                  <div className="min-h-screen bg-gray-50 flex">
                    <SideNav />
                    <div
                      className={`
                flex-1 transition-all duration-300 w-full min-w-0
                ${!isSmTab ? (toggleNav ? "ml-20" : "ml-64") : "ml-0"}
              `}
                    >
                      <PmHeader />
                      <main className="sm:px-6 lg:px-5 w-full max-w-full h-full">
                        <Routes>
                          <Route path="/" element={<AuthenticatedHomeRedirect />} />
                          <Route
                            path={ROUTES.DASHBOARD}
                            element={
                              <RequirePermission permission={PERMISSION_KEYS.DASHBOARD_VIEW}>
                                <Dashboard />
                              </RequirePermission>
                            }
                          />

                          <Route
                            path={ROUTES.CLIENTS}
                            element={
                              <RequirePermission
                                permission={[...CLIENTS_ROUTE_INDEX_ANY_KEYS]}
                                requireAll={false}
                              >
                                <Clients />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={ROUTES.CLIENTS_ADD}
                            element={
                              <RequirePermission
                                permission={[
                                  PERMISSION_KEYS.CLIENT_VIEW,
                                  PERMISSION_KEYS.CLIENTS_CREATE,
                                ]}
                              >
                                <Clients />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={`${ROUTES.CLIENTS_EDIT}/:id`}
                            element={
                              <RequirePermission
                                permission={[
                                  PERMISSION_KEYS.CLIENT_VIEW,
                                  PERMISSION_KEYS.CLIENTS_EDIT,
                                ]}
                              >
                                <Clients />
                              </RequirePermission>
                            }
                          />

                          <Route
                            path={ROUTES.PROJECTS_MANAGEMENT_ADD}
                            element={
                              <RequirePermission
                                permission={PERMISSION_KEYS.PROJECTS_SETTINGS_MANAGE}
                              >
                                <Navigate to={ROUTES.PROJECTS_ADD} replace />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={`${ROUTES.PROJECTS_MANAGEMENT_EDIT}/:id`}
                            element={
                              <RequirePermission
                                permission={PERMISSION_KEYS.PROJECTS_SETTINGS_MANAGE}
                              >
                                <LegacySettingsProjectEditRedirect />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={ROUTES.TEAMS}
                            element={
                              <RequirePermission permission={PERMISSION_KEYS.TEAMS_VIEW}>
                                <Teams />
                              </RequirePermission>
                            }
                          />

                          <Route
                            path={ROUTES.ISSUES}
                            element={
                              <RequirePermission
                                permission={[...WORK_ITEMS_SIDEBAR_ANY_KEYS]}
                                requireAll={false}
                              >
                                <Issues />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path="/issues/work-items/:workItemId"
                            element={
                              <RequirePermission
                                permission={[...WORK_ITEMS_SIDEBAR_ANY_KEYS]}
                                requireAll={false}
                              >
                                <WorkItemSharedDetailPage />
                              </RequirePermission>
                            }
                          />

                          <Route
                            path={ROUTES.TIME_ENTRY}
                            element={
                              <RequirePermission
                                permission={[...TIME_ENTRY_TAB_VIEW_KEYS]}
                                requireAll={false}
                              >
                                <TimeEntry />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={ROUTES.REPORTS}
                            element={
                              <RequirePermission
                                permission={PERMISSION_KEYS.REPORTS_LOCK_AUDIT_VIEW}
                              >
                                <Reports />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={ROUTES.BILLING_CONFIGURATION}
                            element={
                              <RequirePermission permission={PERMISSION_KEYS.BILLING_VIEW}>
                                <BillingConfiguration />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={ROUTES.BILLING_EXPORTS}
                            element={
                              <RequirePermission permission={PERMISSION_KEYS.QB_BILLING_VIEW}>
                                <Exports />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={ROUTES.BILLING_SAAS}
                            element={
                              <RequirePermission permission={PERMISSION_KEYS.SAAS_BILLING_VIEW}>
                                <SaasBilling />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={ROUTES.USERS}
                            element={
                              <RequirePermission permission={PERMISSION_KEYS.USERS_VIEW}>
                                <Users />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={ROUTES.USERS_ADD}
                            element={
                              <RequirePermission
                                permission={[
                                  PERMISSION_KEYS.USERS_VIEW,
                                  PERMISSION_KEYS.USERS_CREATE,
                                ]}
                              >
                                <Users />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={`${ROUTES.USERS_EDIT}/:id`}
                            element={
                              <RequirePermission
                                permission={[
                                  PERMISSION_KEYS.USERS_VIEW,
                                  PERMISSION_KEYS.USERS_EDIT,
                                ]}
                              >
                                <Users />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={ROUTES.ALL_USERS}
                            element={
                              <RequirePermission permission={PERMISSION_KEYS.USERS_VIEW}>
                                <Users />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path="/users/:id"
                            element={
                              <RequirePermission
                                permission={[
                                  PERMISSION_KEYS.USERS_VIEW,
                                  PERMISSION_KEYS.USERS_EDIT,
                                ]}
                              >
                                <UsersBareIdRedirect />
                              </RequirePermission>
                            }
                          />

                          <Route
                            path="/projects"
                            element={
                              <RequirePermission permission={PERMISSION_KEYS.PROJECTS_VIEW}>
                                <ProjectsRoot />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={ROUTES.PROJECTS_ADD}
                            element={
                              <RequirePermission
                                permission={[
                                  PERMISSION_KEYS.PROJECTS_VIEW,
                                  PERMISSION_KEYS.PROJECTS_CREATE,
                                ]}
                              >
                                <ProjectsRoot />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={`${ROUTES.PROJECTS_EDIT}/:id`}
                            element={
                              <RequirePermission
                                permission={[
                                  PERMISSION_KEYS.PROJECTS_VIEW,
                                  PERMISSION_KEYS.PROJECTS_EDIT,
                                ]}
                              >
                                <ProjectsRoot />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path="/projects/:projectSlug"
                            element={
                              <RequirePermission
                                permission={[...PROJECTS_ROUTE_WORKSPACE_ANY_KEYS]}
                                requireAll={false}
                              >
                                <Projects />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path="/projects/:projectSlug/work-items/:workItemId"
                            element={
                              <RequirePermission
                                permission={[...WORK_ITEMS_SIDEBAR_ANY_KEYS]}
                                requireAll={false}
                              >
                                <WorkItemDetailPage />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={ROUTES.PROJECTS_MANAGEMENT}
                            element={
                              <RequirePermission
                                permission={PERMISSION_KEYS.PROJECTS_SETTINGS_MANAGE}
                              >
                                <Navigate to={ROUTES.PROJECTS} replace />
                              </RequirePermission>
                            }
                          />

                          <Route
                            path={ROUTES.SETTINGS}
                            element={
                              <RequirePermission
                                permission={[
                                  PERMISSION_KEYS.TAGS_VIEW,
                                  PERMISSION_KEYS.SETTINGS_ROLE_PERMISSION,
                                  PERMISSION_KEYS.PROJECTS_SETTINGS_MANAGE,
                                ]}
                                requireAll={false}
                              >
                                <Settings />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path="/settings/client-tag-mapping"
                            element={
                              <RequirePermission permission={PERMISSION_KEYS.TAGS_VIEW}>
                                <TabMapping />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path="/settings/vendor-tag-mapping"
                            element={
                              <RequirePermission permission={PERMISSION_KEYS.TAGS_VIEW}>
                                <VendorTagMapping />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path="/settings/role-permission"
                            element={
                              <RequirePermission
                                permission={PERMISSION_KEYS.SETTINGS_ROLE_PERMISSION}
                              >
                                <RolePermission />
                              </RequirePermission>
                            }
                          />
                          <Route
                            path={ROUTES.SETTINGS_SYSTEM_LISTS}
                            element={
                              <RequirePermission
                                permission={[...SETTINGS_SYSTEM_LISTS_ANY_KEYS]}
                                requireAll={false}
                              >
                                <SystemListsSettings />
                              </RequirePermission>
                            }
                          />
                          <Route path={ROUTES.PROFILE_EDIT} element={<EditProfile />} />
                          <Route path={ROUTES.PROFILE_TEAM_PROJECTS} element={<TeamProjects />} />
                          <Route path={ROUTES.CHANGE_PASSWORD} element={<ChangePassword />} />
                          <Route path="*" element={<PageNotFound />} />
                        </Routes>
                      </main>
                    </div>
                  </div>
                </>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
